import { Box, Flex, Text, Tooltip } from '@modusign/design-system';
import { FieldType } from '@modusign/react-signing/types';
import React from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useToggle } from 'react-use';

import { ArrowButton } from '@app/components/Buttons';
import CircleMarker from '@app/components/CircleMarker';
import FieldLocatorBadge from '@app/components/FieldLocatorBadge';
import SetupButton from '@app/components/SetupButton';

type SetupButtonDropdownItemPropType<T extends FieldType> = {
  type: T;
  icon: React.ReactElement;
  text: string;
  count: number;
};

interface SetupButtonPropsBase<T extends FieldType> {
  readOnly: boolean;
  items: SetupButtonDropdownItemPropType<T>[];
  type: T | null;
  placeholder: string;
  placeholderIcon?: React.ReactElement;
  testId: string;
}

interface SetupButtonProps<T extends FieldType> extends SetupButtonPropsBase<T> {
  tooltip: React.ReactElement;
  onClick: () => void;
  onSelect: (type: T) => void;
}

const defaultProps = {
  children: null,
  leftIcon: <CircleMarker />,
  readOnly: false,
  testId: 'setup-button-dropdown',
};

function SetupButtonDropdown<T extends FieldType>(props: SetupButtonProps<T>) {
  const [isOpen, toggleIsOpen] = useToggle(false);
  const { readOnly, items, onSelect } = props;

  function handleToggle() {
    toggleIsOpen();
  }
  function handleSelect(type: T) {
    onSelect(type);
    toggleIsOpen(false);
  }

  return (
    <>
      {readOnly ? (
        items.map((item) => (
          <SetupButton count={item.count} key={item.type} readOnly text={item.text} />
        ))
      ) : (
        <OutsideClickHandler disabled={!isOpen} onOutsideClick={() => toggleIsOpen(false)}>
          <SetupButtonDropdownButton {...props} onToggleClick={handleToggle} />
          {isOpen && <SetupButtonDropdownSelect {...props} onSelect={handleSelect} />}
        </OutsideClickHandler>
      )}
    </>
  );
}

interface SetupButtonDropdownButtonProps<T extends FieldType> extends SetupButtonPropsBase<T> {
  tooltip: React.ReactElement;
  onClick: () => void;
  onToggleClick: () => void;
}

function SetupButtonDropdownButton<T extends FieldType>({
  readOnly,
  placeholderIcon,
  placeholder,
  testId,
  type,
  items,
  tooltip,
  onClick,
  onToggleClick,
}: SetupButtonDropdownButtonProps<T>) {
  const handleClick = () => {
    if (!readOnly && type !== null) {
      onClick();
    }
    if (!type) {
      onToggleClick();
    }
  };

  const selectedItem = type ? items.find((f) => f.type === type) : null;
  return (
    <Tooltip content={<Text>{tooltip}</Text>} disabled={readOnly || !tooltip} placement="right">
      <Box
        data-testid={testId}
        justifyContent="space-between"
        position="relative"
        sx={
          readOnly
            ? {
                bg: 'white',
                height: '36px',
                paddingBottom: '5px',
                textAlign: 'left',
                marginBottom: 2,
                outline: 'none',
                width: '100%',
              }
            : {
                bg: 'white',
                border: '1px solid',
                borderColor: 'buttonBorderNeutralBase',
                borderRadius: '4px',
                cursor: 'pointer',
                marginBottom: 2,
                outline: 'none',
                textAlign: 'left',
                width: '100%',
                height: '36px',
                [type ?? '&:hover']: type ?? {
                  bg: 'lightGray50',
                },
              }
        }
        tabIndex={0}
      >
        <Flex
          alignItems="center"
          height="100%"
          justifyContent="space-between"
          padding="8px 8px 8px 12px"
          position="relative"
          sx={{
            '&:hover': {
              bg: type !== null ? 'lightGray50' : undefined,
            },
          }}
          width="calc(100% - 35px)"
          onClick={handleClick}
        >
          <Box width="100%">
            {placeholderIcon && (
              <Box mr={3} sx={{ float: 'left' }} textAlign="center" width="20px">
                {selectedItem ? selectedItem.icon : placeholderIcon}
              </Box>
            )}
            <Text
              as="span"
              color="textNeutralLight"
              fontSize="md"
              fontWeight="medium"
              lineHeight="18px"
            >
              {selectedItem ? selectedItem.text : placeholder}
            </Text>
          </Box>
          {selectedItem && selectedItem.count > 0 && (
            <FieldLocatorBadge>{selectedItem.count}</FieldLocatorBadge>
          )}
        </Flex>
        <ArrowButton
          aria-label="field-locator-signature-dropdown"
          direction="bottom"
          sx={{
            border: 'none',
            borderLeft: '1px solid',
            borderLeftColor: 'lightGray300',
            margin: 0,
            padding: 0,
            position: 'absolute',
            height: '100%',
            right: 0,
            top: 0,
            '&:hover': {
              bg: 'lightGray50',
            },
            borderRadius: '0 3px 3px 0',
          }}
          onClick={(e) => {
            onToggleClick();
            e.preventDefault();
          }}
        />
      </Box>
    </Tooltip>
  );
}

interface SetupButtonDropdownSelectProps<T extends FieldType> {
  items: SetupButtonDropdownItemPropType<T>[];
  onSelect: (type: T) => void;
}

function SetupButtonDropdownSelect<T extends FieldType>({
  items,
  onSelect,
}: SetupButtonDropdownSelectProps<T>) {
  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'lightGray300',
          borderRadius: '4px',
          left: 0,
          marginTop: 0,
          minWidth: 184,
          overflow: 'hidden',
          position: 'absolute',
          textAlign: 'left',
          top: 0,
          zIndex: 2,
        }}
      >
        <Box
          as="ul"
          sx={{
            position: 'relative',
            width: '100%',
          }}
        >
          {items.map((item) => {
            return (
              <SetupButtonDropdownSelectItem item={item} key={item.type} onSelect={onSelect} />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function SetupButtonDropdownSelectItem<T extends FieldType>({
  item,
  onSelect,
}: {
  item: SetupButtonDropdownItemPropType<T>;
  onSelect: (type: T) => void;
}) {
  return (
    <Box as="li" key={item.type} width="100%">
      <Box
        as="button"
        sx={{
          color: 'textNeutralVeryLight',
          height: 36,
          padding: '8px 16px 8px 12px',
          position: 'relative',
          textAlign: 'left',
          width: '100%',
          '&:hover': {
            backgroundColor: 'lightGray50',
            color: 'textNeutralLight',
          },
        }}
        type="button"
        onClick={() => onSelect(item.type)}
      >
        <Flex alignItems="center" justifyContent="flex-start">
          <Flex alignItems="center" justifyContent="center" width="20px">
            {item.icon}
          </Flex>
          <Text color="inherit" fontSize="md" fontWeight="medium" ml={2}>
            {item.text}
          </Text>
        </Flex>
      </Box>
    </Box>
  );
}

SetupButtonDropdown.defaultProps = defaultProps;

export default SetupButtonDropdown;
